import type { CallExtraction, InboundCall } from '../api/callsApi';

export function getPatientName(call: InboundCall): string | null {
  const ex = call.extractionJson;
  return (
    ex.patient_name ??
    ex.caller_name ??
    ex.appointment_ref?.patient_name ??
    call.summaryJson.caller ??
    null
  );
}

export function getComplaint(call: InboundCall): string | null {
  const ex = call.extractionJson;
  return ex.reason ?? call.summaryJson.reason ?? ex.service_interest ?? ex.procedure ?? null;
}

export function getPainLevel(call: InboundCall): string | null {
  const ex = call.extractionJson;
  const value = ex.pain_level ?? ex.painLevel;
  return typeof value === 'string' && value.trim() ? value : null;
}

export function getCallbackNumber(call: InboundCall): string | null {
  return call.extractionJson.callback_number ?? call.summaryJson.callback ?? call.callerNumber;
}

// Ordered field list covering every key across all 8 Retell call templates.
// The detail panel filters this to whichever keys are actually present on a
// given call's extractionJson, so a template that populates 8 fields shows 8.
export const POST_CALL_FIELD_DEFS: { key: keyof CallExtraction; label: string }[] = [
  { key: 'patient_name', label: 'Patient name' },
  { key: 'patient_dob', label: 'Patient DOB' },
  { key: 'patient_phone', label: 'Patient phone' },
  { key: 'caller_name', label: 'Caller name' },
  { key: 'callback_number', label: 'Callback number' },
  { key: 'callback_language', label: 'Callback language' },
  { key: 'reason', label: 'Reason for call' },
  { key: 'pain_level', label: 'Pain level' },
  { key: 'urgency', label: 'Urgency' },
  { key: 'procedure', label: 'Procedure' },
  { key: 'teeth', label: 'Teeth' },
  { key: 'radiographs', label: 'Radiographs' },
  { key: 'intent', label: 'Intent' },
  { key: 'handled_by', label: 'Handled by' },
  { key: 'transfer_attempted', label: 'Transfer attempted' },
  { key: 'transfer_result', label: 'Transfer result' },
  { key: 'priority', label: 'Priority' },
  { key: 'patient_status', label: 'Patient status' },
  { key: 'preferred_window', label: 'Preferred window' },
  { key: 'insurance_carrier', label: 'Insurance carrier' },
  { key: 'sms_consent', label: 'SMS consent' },
  { key: 'service_interest', label: 'Service interest' },
  { key: 'lead_temperature', label: 'Lead temperature' },
  { key: 'commitment', label: 'Commitment' },
  { key: 'objection', label: 'Objection' },
  { key: 'referral_source', label: 'Referral source' },
  { key: 'intake_complete', label: 'Intake complete' },
  { key: 'saved', label: 'Saved' },
  { key: 'cancel_reason', label: 'Cancel reason' },
  { key: 'same_day', label: 'Same day' },
  { key: 'referring_office', label: 'Referring office' },
  { key: 'referring_doctor', label: 'Referring doctor' },
  { key: 'referring_office_email', label: 'Referring office email' },
  { key: 'office_callback', label: 'Office callback' },
  { key: 'source', label: 'Source' },
  { key: 'offer_discussed', label: 'Offer discussed' },
];

function formatFieldValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'string') return value.trim() ? value : null;
  return String(value);
}

export interface PostCallFieldRow {
  label: string;
  value: string;
}

// Filters POST_CALL_FIELD_DEFS down to keys with a real value for this call,
// plus the appointment_ref sub-object (patient/date/provider) when present.
export function getPostCallFields(call: InboundCall): PostCallFieldRow[] {
  const ex = call.extractionJson;
  const rows: PostCallFieldRow[] = [];

  for (const { key, label } of POST_CALL_FIELD_DEFS) {
    const formatted = formatFieldValue(ex[key]);
    if (formatted) rows.push({ label, value: formatted });
  }

  if (ex.appointment_ref) {
    const { patient_name, appt_date, provider } = ex.appointment_ref;
    if (patient_name) rows.push({ label: 'Appointment patient', value: patient_name });
    if (appt_date) rows.push({ label: 'Appointment date', value: appt_date });
    if (provider) rows.push({ label: 'Appointment provider', value: provider });
  }

  return rows;
}

// ── Outcome, duration, disconnection ─────────────────────────────────────────

const HANDLED_BY_LABEL: Record<string, string> = {
  agent: 'Handled by agent',
  transferred: 'Transferred to team',
  captured_for_callback: 'Callback captured',
};

const COMMITMENT_LABEL: Record<string, string> = {
  booked_intent: 'Wants to book',
  callback_requested: 'Callback requested',
  not_committed: 'Interested — not committed',
  not_a_lead: 'Not a lead',
};

function humanize(value: string): string {
  const s = value.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export interface OutcomeLabel {
  label: string;
  kind: 'success' | 'error' | 'neutral';
}

// Older templates post a free-text `outcome`; current ones post the shared
// taxonomy (commitment / handled_by / intent). Prefer the most specific.
export function getOutcome(call: InboundCall): OutcomeLabel | null {
  if (call.outcome) return { label: call.outcome, kind: call.outcomeKind };
  if (call.outcomeKind === 'error') return { label: 'Call failed', kind: 'error' };
  if (call.commitment) {
    return {
      label: COMMITMENT_LABEL[call.commitment] ?? humanize(call.commitment),
      kind: call.commitment === 'booked_intent' ? 'success' : 'neutral',
    };
  }
  if (call.handledBy) {
    return { label: HANDLED_BY_LABEL[call.handledBy] ?? humanize(call.handledBy), kind: 'neutral' };
  }
  if (call.intent) return { label: humanize(call.intent), kind: 'neutral' };
  return null;
}

// Retell disconnection reasons that mean the call didn't end the normal way.
// Normal endings (caller/agent hung up, transferred) return null.
const DISCONNECT_LABEL: Record<string, string> = {
  inactivity: 'Ended — caller went silent',
  machine_detected: 'Reached voicemail / machine',
  max_duration_reached: 'Cut off at max call length',
  concurrency_limit_reached: 'Rejected — too many simultaneous calls',
  no_valid_payment: 'Rejected — Retell billing issue',
  scam_detected: 'Flagged as spam by Retell',
  dial_busy: 'Line busy',
  dial_failed: 'Call failed to connect',
  dial_no_answer: 'No answer',
  voicemail_reached: 'Reached voicemail',
};
const NORMAL_DISCONNECTS = new Set(['user_hangup', 'agent_hangup', 'call_transfer']);

export function getDisconnectionIssue(call: InboundCall): string | null {
  const reason = call.disconnectionReason;
  if (!reason || NORMAL_DISCONNECTS.has(reason)) return null;
  if (reason.startsWith('error')) return `Retell error (${reason.replace(/^error_/, '')})`;
  return DISCONNECT_LABEL[reason] ?? humanize(reason);
}

export function getDurationLabel(call: InboundCall): string | null {
  if (!call.endedAt) return null;
  const secs = Math.round(
    (new Date(call.endedAt).getTime() - new Date(call.receivedAt).getTime()) / 1000,
  );
  if (!Number.isFinite(secs) || secs < 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}
